import { Routes } from '@angular/router'
import {
  BreakpointAction,
  ChoiceFunction,
  EligibleAppleWatchBreakpointOption,
  FeelingStressedBreakpointOption,
  QuizDataInput,
  WearWatchToBedOption,
  WhatToTrackWithDeviceOption,
  WhichWearableBreakpointOption,
} from '@naturalcycles/shared'
import { allQuizSections } from '@src/app/constants/quiz'
import { addPageInSectionAfterPage, filterPagesFromSection } from '@src/app/core/util/quiz.util'
import { Page } from '@src/app/shared/typings/enum/pages'
import { DefaultSections } from '@src/app/shared/typings/enum/quiz'

const commonTransformFunctionWhichWearable: ChoiceFunction<Page, QuizDataInput> = flow => {
  flow[allQuizSections.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.whatToTrack,
    Page.sleepAndLifestyle,
    Page.wakeUpTime,
    Page.snooze,
    Page.dailyRoutine,
    Page.resultOverview,
  ]
  return flow
}

export const whichWearable371: BreakpointAction<
  WhichWearableBreakpointOption,
  Page,
  QuizDataInput
> = {
  [WhichWearableBreakpointOption.appleWatch]: flow => {
    flow[allQuizSections.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.eligibleAppleWatch,
      Page.sleepAndLifestyle,
      Page.wakeUpTime,
      Page.snooze,
      Page.dailyRoutine,
      Page.resultOverview,
    ]
    return flow
  },
  [WhichWearableBreakpointOption.ouraRing]: flow => {
    flow[allQuizSections.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.wakeUpTime,
      Page.snooze,
      Page.dailyRoutine,
      Page.resultOverview,
    ]
    return flow
  },
  [WhichWearableBreakpointOption.skip]: commonTransformFunctionWhichWearable,
}

const commonTransformFunction: ChoiceFunction<Page, QuizDataInput> = flow => {
  flow[allQuizSections.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.eligibleAppleWatch,
    Page.wearYourWatchToSleep,
    Page.whatToTrack,
    Page.sleepAndLifestyle,
    Page.wakeUpTime,
    Page.snooze,
    Page.dailyRoutine,
    Page.resultOverview,
  ]
  return flow
}

export const eligibleAppleWatch371: BreakpointAction<
  EligibleAppleWatchBreakpointOption,
  Page,
  QuizDataInput
> = {
  [EligibleAppleWatchBreakpointOption.yes]: commonTransformFunction,
  [EligibleAppleWatchBreakpointOption.no]: flow => {
    flow[allQuizSections.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.eligibleAppleWatch,
      Page.whatToTrack,
      Page.wakeUpTime,
      Page.snooze,
      Page.dailyRoutine,
      Page.resultOverview,
    ]
    return flow
  },
}

const yesTransformFunctionWearWatchToSleep: ChoiceFunction<Page, QuizDataInput> = flow => {
  flow[allQuizSections.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.eligibleAppleWatch,
    Page.wearYourWatchToSleep,
    Page.sleepAndLifestyle,
    Page.wakeUpTime,
    Page.snooze,
    Page.dailyRoutine,
    Page.resultOverview,
  ]
  return flow
}

const noTransformFunctionWearWatchToSleep: ChoiceFunction<Page, QuizDataInput> = (flow, _data) => {
  flow[allQuizSections.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.eligibleAppleWatch,
    Page.wearYourWatchToSleep,
    Page.whatToTrack,
    Page.sleepAndLifestyle,
    Page.wakeUpTime,
    Page.snooze,
    Page.dailyRoutine,
    Page.resultOverview,
  ]
  return flow
}

export const wearWatchToSleep: BreakpointAction<WearWatchToBedOption, Page, QuizDataInput> = {
  [WearWatchToBedOption.yes]: yesTransformFunctionWearWatchToSleep,
  [WearWatchToBedOption.noButOpenToIt]: yesTransformFunctionWearWatchToSleep,
  [WearWatchToBedOption.no]: noTransformFunctionWearWatchToSleep,
}

const fertilityAndMoreTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, _data) => {
  const pagesToAdd = [
    Page.biggestStressor,
    Page.feelingStressed,
    Page.healthGoals,
    Page.spendingDaysInfo,
  ]
  pagesToAdd.forEach(p => {
    flow = addPageInSectionAfterPage(flow, p, Page.dailyRoutine, DefaultSections.routine)
  })

  return flow
}

const onlyCycleTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, _data) => {
  flow = filterPagesFromSection(
    flow,
    [Page.biggestStressor, Page.feelingStressed, Page.healthGoals, Page.spendingDaysInfo],
    DefaultSections.routine,
  )
  return flow
}
export const whatToTrack371: BreakpointAction<WhatToTrackWithDeviceOption, Page, QuizDataInput> = {
  [WhatToTrackWithDeviceOption.cycle]: onlyCycleTransformFunction,
  [WhatToTrackWithDeviceOption.cycleAndMetrics]: fertilityAndMoreTransformFunction,
  [WhatToTrackWithDeviceOption.notSure]: onlyCycleTransformFunction,
}

const feelingStressedTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, _data) => {
  flow[allQuizSections.routine] = [
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.resultOverview,
  ]
  return flow
}

export const feelingStressed371: BreakpointAction<
  FeelingStressedBreakpointOption,
  Page,
  QuizDataInput
> = {
  [FeelingStressedBreakpointOption.always]: feelingStressedTransformFunction,
  [FeelingStressedBreakpointOption.timeToTime]: feelingStressedTransformFunction,
  [FeelingStressedBreakpointOption.noStress]: feelingStressedTransformFunction,
}

export const ab371Routes: Routes = [
  {
    path: Page.whatToTrack,
    loadComponent: () =>
      import('./pages/what-to-track/what-to-track.page.component').then(m => m.WhatToTrackPage),
  },
  {
    path: Page.wearYourWatchToSleep,
    loadComponent: () =>
      import('./pages/wear-watch-to-sleep/wear-watch-to-sleep.page.component').then(
        m => m.WearWatchToSleepPage,
      ),
  },
  {
    path: Page.sleepAndLifestyle,
    loadComponent: () =>
      import('./pages/sleep-and-lifestyle/sleep-and-lifestyle.page.component').then(
        m => m.SleepAndLifestylePage,
      ),
  },
  {
    path: Page.dailyRoutine,
    loadComponent: () =>
      import('./pages/daily-routine/daily-routine.page.component').then(m => m.DailyRoutinePage),
  },
  {
    path: Page.wakeUpTime,
    loadComponent: () =>
      import('./pages/wake-up-time/wake-up-time.page.component').then(m => m.WakeUpTimePage),
  },
  {
    path: Page.snooze,
    loadComponent: () => import('./pages/snooze/snooze.page.component').then(m => m.SnoozePage),
  },
].map(r => ({
  ...r,
  data: {
    page: r.path,
  },
}))
